import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useIsMobile } from "@/hooks/useIsMobile";
import LoaderV2 from "@/pages/InvoiceManagement/components/LoaderV2";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import { useToast } from "@/pages/InvoiceManagement/hooks/useToast";
import { useRaiseRfqQuery, useRfqAction } from "./api/rfqMutations";
import { useRfqDetail } from "./api/rfqQueries";
import ConfirmResponseModal, {
  primaryLabelFor,
} from "./components/detail/ConfirmResponseModal";
import DeclineRfqModal from "./components/detail/DeclineRfqModal";
import QuestionComposer from "./components/detail/QuestionComposer";
import QuestionThread from "./components/detail/QuestionThread";
import ResponseFooter from "./components/detail/ResponseFooter";
import RfqDetailHeader from "./components/detail/RfqDetailHeader";
import RfqLineCards from "./components/detail/RfqLineCards";
import RfqLineTable from "./components/detail/RfqLineTable";
import RfqStatStrip from "./components/detail/RfqStatStrip";
import SubmittedPanel from "./components/detail/SubmittedPanel";
import WithdrawRfqModal from "./components/detail/WithdrawRfqModal";
import { RFQ_TAB_PATH } from "./constants/paths";
import { useNow } from "./hooks/useNow";
import { useRfqResponseForm } from "./hooks/useRfqResponseForm";
import type {
  DeclineRfqPayload,
  VendorRfqDetail,
  VendorRfqLine,
} from "./types";
import { actionErrorMessage, apiErrorMessage, rfqApiError, rfqLoadErrorMessage } from "./utils/apiError";
import { nextAttachmentExpiry } from "./utils/attachments";
import { questionsLeft, quotaCopy } from "./utils/quota";
import { submissionOf, summarise } from "./utils/response";
import { describeRfq } from "./utils/rfqCardCopy";

const QUESTION_POLL_MS = 15000;
const LINK_REFRESH_MARGIN_MS = 60000;
const LINK_REFRESH_FLOOR_MS = 30000;
const MAX_TIMEOUT_MS = 2147483647;
const V2_LABEL = {
  fontSize: "10px",
  color: V2_COLORS.textSecondary,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
} as const;

export default function RfqDetail() {
  const { rfqId } = useParams<{ rfqId: string }>();
  const now = useNow();
  const [pollInterval, setPollInterval] = useState<number | false>(false);
  const { data, isLoading, error, refetch } = useRfqDetail(rfqId, {
    refetchInterval: pollInterval,
  });
  const isMobile = useIsMobile();
  const awaitingAnswer = Boolean(
    data &&
    data.clock.state === "RUNNING" &&
    (data.queries ?? []).some((query) => query.status === "OPEN") &&
    new Date(data.respond_by).getTime() > now,
  );

  const linkExpiry = data ? nextAttachmentExpiry(data.lines) : null;

  useEffect(() => {
    setPollInterval(awaitingAnswer ? QUESTION_POLL_MS : false);
  }, [awaitingAnswer]);

  useEffect(() => {
    if (linkExpiry === null) return undefined;
    const delay = Math.min(Math.max(linkExpiry - LINK_REFRESH_MARGIN_MS - Date.now(), LINK_REFRESH_FLOOR_MS), MAX_TIMEOUT_MS);
    const timer = setTimeout(() => refetch(), delay);
    return () => clearTimeout(timer);
  }, [linkExpiry, refetch]);

  if (isLoading) return <LoaderV2 />;
  if (!data) {
    if (rfqApiError(error)?.error_code === "RFQ_NOT_FOUND") {
      return (
        <Box sx={{ p: 3 }}>
          <Typography role="alert" sx={{ mb: 1.5 }}>
            This RFQ is no longer available to you.
          </Typography>
          <Button variant="outlined" component={Link} to={RFQ_TAB_PATH}>
            Back to RFQs
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ p: 3 }}>
        <Typography role="alert" sx={{ mb: 1.5 }}>
          {rfqLoadErrorMessage(error, "Could not load this RFQ. Please try again.")}
        </Typography>
        <Button variant="outlined" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <RfqDetailBody
      key={`${data.id}:${data.version}`}
      detail={data}
      now={now}
      isMobile={isMobile}
    />
  );
}

interface RfqDetailBodyProps {
  detail: VendorRfqDetail;
  now: number;
  isMobile: boolean;
}

function RfqDetailBody({ detail, now, isMobile }: RfqDetailBodyProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const act = useRfqAction();
  const raise = useRaiseRfqQuery();
  const form = useRfqResponseForm(detail);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [composer, setComposer] = useState<{ lineId?: number } | null>(null);

  const { windowOpen } = describeRfq(detail, now);
  const submission = submissionOf(detail);
  const allowed = detail.allowed_actions ?? [];
  const canRespond = allowed.includes("ACCEPT_AT_ASK") || allowed.includes("BID");
  const canDecline = windowOpen && allowed.includes("DECLINE");
  const canWithdraw = windowOpen && allowed.includes("WITHDRAW");
  const askAllowed = windowOpen && allowed.includes("QUERY");
  const showForm = detail.status === "OPEN";
  const readOnly = !windowOpen || !canRespond;
  const hasResponse = showForm || Boolean(submission);
  const bidTotal = showForm ? form.summary.total : submission?.bid_total;
  const submitting = act.isLoading;
  const questionsRemaining = questionsLeft(detail.query_quota);
  const showAsk = askAllowed || (windowOpen && canRespond && questionsRemaining === 0);
  const canAsk = askAllowed && questionsRemaining > 0;
  const queries = detail.queries ?? [];
  const requestQuestions = queries.filter((query) => !query.line_id);
  const showRequestComposer =
    composer !== null && composer.lineId === undefined;

  const confirm = () => {
    const mode = form.summary.mode;
    const base = { id: detail.id, reviewed_confirmation: true as const, expected_version: detail.version };
    act.mutate(
      mode === "BID" ? { ...base, action: "BID", lines: form.payload } : { ...base, action: "ACCEPT_AT_ASK" },
      {
        onSuccess: () => {
          showToast({
            type: "success",
            message:
              mode === "BID"
                ? "Bid submitted · under evaluation"
                : "Accepted at asking price · under evaluation",
          });
          navigate(`${RFQ_TAB_PATH}&status=submitted`);
        },
        onError: (error: unknown) => {
          setConfirmOpen(false);
          showToast({
            type: "error",
            message: actionErrorMessage(error, "Could not submit your response"),
          });
        },
      },
    );
  };

  const sendQuestion = (text: string) => {
    const sender = composer;
    const lineId = sender?.lineId;
    raise.mutate(
      { id: detail.id, text, ...(lineId !== undefined ? { line_id: lineId } : {}) },
      {
        onSuccess: () => {
          setComposer((current) => (current === sender ? null : current));
          showToast({
            type: "success",
            message: "Question sent · the Supply Manager will answer here",
          });
        },
        onError: (error: unknown) =>
          showToast({
            type: "error",
            message: apiErrorMessage(error, "Could not send your question"),
          }),
      },
    );
  };

  const confirmDecline = (payload: DeclineRfqPayload) => {
    act.mutate(
      { id: detail.id, action: "DECLINE", expected_version: detail.version, ...payload },
      {
        onSuccess: () => {
          showToast({
            type: "success",
            message: "RFQ declined · the Supply Manager has been told",
          });
          navigate(`${RFQ_TAB_PATH}&status=closed`);
        },
        onError: (error: unknown) => {
          setDeclineOpen(false);
          showToast({
            type: "error",
            message: actionErrorMessage(error, "Could not decline this RFQ"),
          });
        },
      },
    );
  };

  const confirmWithdraw = () => {
    act.mutate(
      { id: detail.id, action: "WITHDRAW", expected_version: detail.version },
      {
        onSuccess: () => {
          setWithdrawOpen(false);
          showToast({
            type: "success",
            message: "Response withdrawn · edit and submit again before the deadline",
          });
        },
        onError: (error: unknown) => {
          setWithdrawOpen(false);
          showToast({
            type: "error",
            message: actionErrorMessage(error, "Could not withdraw your response"),
          });
        },
      },
    );
  };

  const composerFor = (
    key: string,
    label: string,
    placeholder: string,
    plain = false,
  ) => (
    <QuestionComposer
      key={key}
      label={label}
      placeholder={placeholder}
      sending={raise.isLoading}
      onSend={sendQuestion}
      onCancel={() => setComposer(null)}
      plain={plain}
    />
  );

  const lineExtras = (line: VendorRfqLine) => {
    const questions = queries.filter((query) => query.line_id === line.line_id);
    const open = composer?.lineId === line.line_id;
    if (questions.length === 0 && !open) return null;
    return (
      <Stack spacing={1.5}>
        <QuestionThread queries={questions} />
        {open &&
          composerFor(
            String(line.line_id),
            `Question on ${line.trade} · ${line.room}`,
            "e.g. Is scaffolding included above 12ft?",
          )}
      </Stack>
    );
  };

  const interaction = {
    onAskLine: canAsk
      ? (line: VendorRfqLine) => setComposer({ lineId: line.line_id })
      : undefined,
    lineExtras,
  };
  const lineProps = hasResponse
    ? {
        detail,
        rates: form.rates,
        summary: form.summary,
        onRateChange: form.setRate,
        readOnly: !showForm || readOnly,
        ...interaction,
      }
    : {
        detail,
        rates: {},
        summary: summarise(detail, {}),
        onRateChange: form.setRate,
        readOnly: true,
        ...interaction,
      };
  const lines = isMobile ? (
    <RfqLineCards {...lineProps} />
  ) : (
    <RfqLineTable {...lineProps} />
  );

  const actionButtons =
    showAsk || canDecline ? (
      <>
        {showAsk && (
          <Box sx={{ position: "relative", display: "flex", flexDirection: "column" }}>
            <Button
              variant="outlined"
              disabled={!canAsk}
              aria-describedby="rfq-query-quota"
              onClick={() => setComposer({})}
              sx={{ textTransform: "none", fontWeight: 600, height: 40 }}
            >
              Ask about this RFQ
            </Button>
            <Typography
              id="rfq-query-quota"
              data-testid="query-quota"
              role="status"
              fontSize="12px"
              sx={{ color: V2_COLORS.textSecondary, mt: 0.5, ...(isMobile ? {} : { position: "absolute", top: "100%", left: 0, whiteSpace: "nowrap" }) }}
            >
              {quotaCopy(detail.query_quota)}
            </Typography>
          </Box>
        )}
        {canDecline && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeclineOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600, height: 40 }}
          >
            Decline RFQ
          </Button>
        )}
      </>
    ) : null;
  const secondaryActions =
    actionButtons && isMobile ? (
      <Box data-testid="rfq-actions" sx={{ display: "grid", gridTemplateColumns: showAsk && canDecline ? "1fr 1fr" : "1fr", gap: 1.5 }}>
        {actionButtons}
      </Box>
    ) : (
      actionButtons
    );

  const requestBlock = (requestQuestions.length > 0 || showRequestComposer) && (
    <Box
      data-testid="rfq-questions"
      sx={{
        backgroundColor: "white",
        border: `1px solid ${V2_COLORS.border}`,
        borderRadius: "8px",
        p: 2,
      }}
    >
      {requestQuestions.length > 0 && (
        <Typography sx={{ ...V2_LABEL, mb: 1 }}>
          Questions about this RFQ
        </Typography>
      )}
      <Stack spacing={1.5}>
        <QuestionThread queries={requestQuestions} />
        {showRequestComposer &&
          composerFor(
            "rfq",
            "Question about the whole RFQ",
            "e.g. Can the site be accessed on Sundays?",
            true,
          )}
      </Stack>
    </Box>
  );

  const footer = showForm && (
    <ResponseFooter
      lineCount={detail.lines.length}
      reviewed={form.reviewed}
      onReviewedChange={form.setReviewed}
      primaryLabel={primaryLabelFor(form.summary)}
      canSubmit={form.canSubmit}
      onSubmit={() => setConfirmOpen(true)}
      isMobile={isMobile}
      readOnly={readOnly}
      submitting={submitting}
      secondaryActions={secondaryActions}
    />
  );

  const content = (
    <Stack spacing={2}>
      <RfqDetailHeader
        rfq={detail}
        now={now}
        actions={showForm || isMobile ? undefined : secondaryActions}
      />
      <RfqStatStrip
        rfq={detail}
        bidTotal={bidTotal}
        now={now}
        isMobile={isMobile}
      />
      {showForm && readOnly && (
        <Alert severity="warning">
          {windowOpen
            ? "Responses are not being accepted for this RFQ right now."
            : "Window closed · responses are no longer accepted for this RFQ."}
        </Alert>
      )}
      {detail.status === "SUBMITTED" && (
        <>
          <SubmittedPanel
            rfq={detail}
            now={now}
            canWithdraw={canWithdraw}
            onWithdraw={() => setWithdrawOpen(true)}
          />
          {isMobile && secondaryActions}
        </>
      )}
      {lines}
      {requestBlock}
    </Stack>
  );

  const modal = (
    <>
      <DeclineRfqModal
        open={declineOpen}
        detail={detail}
        submitting={act.isLoading}
        onClose={() => {
          if (!act.isLoading) setDeclineOpen(false);
        }}
        onConfirm={confirmDecline}
      />
      <WithdrawRfqModal
        open={withdrawOpen}
        submitting={act.isLoading}
        onClose={() => {
          if (!act.isLoading) setWithdrawOpen(false);
        }}
        onConfirm={confirmWithdraw}
      />
      <ConfirmResponseModal
        open={confirmOpen}
        detail={detail}
        summary={form.summary}
        submitting={submitting}
        onClose={() => {
          if (!submitting) setConfirmOpen(false);
        }}
        onConfirm={confirm}
      />
    </>
  );

  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 48px)",
          overflowY: "auto",
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 2 }}>{content}</Box>
        {footer}
        {modal}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {content}
      {footer && <Box sx={{ mt: 2 }}>{footer}</Box>}
      {modal}
    </Box>
  );
}
